import { Application, Container, Sprite, Graphics } from 'pixi.js';
import { TOKENS } from '../tokens';
import { GameClock } from '../timing';
import createSdfCircle from './SdfCircle';

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * PixiJS renderer for a circle-style hit circle with approach ring.
 * Uses cached Graphics layers for performance.
 */
export default class HitCircle extends Container {
  private circle: Container;
  private ring: Sprite;
  private startScale: number;
  private appear: number;
  private pulseStart = 0;
  private pulseTicker?: () => void;
  private readonly pulseDuration = 120; // ms

  constructor(
    private app: Application,
    x: number,
    y: number,
    radius: number,
    approachRadius: number,
    preempt: number,
    fade: number,
    hidden: boolean,
  ) {
    super();
    this.x = x;
    this.y = y;
    this.startScale = (approachRadius / radius) * TOKENS.glowStrong;
    this.appear = GameClock.now();

    const accent = parseInt(TOKENS.accent.slice(1), 16);
    const base = createSdfCircle(radius);
    base.tint = accent;

    const rimWidth = 2;
    const rim = createSdfCircle(radius, radius - rimWidth);
    rim.tint = accent;
    rim.blendMode = 'add';

    // Soft highlight toward the top-left
    const highlight = new Graphics();
    const offset = radius * 0.3;
    highlight
      .circle(-offset, -offset, radius)
      .circle(0, 0, radius)
      .cut()
      .fill({ color: 0xffffff, alpha: 0.15 });
    highlight.blendMode = 'add';

    const circle = new Container();
    circle.addChild(base, rim, highlight);
    this.circle = circle;
    this.addChild(circle);

    const approachOuter = radius + TOKENS.approach.width / 2;
    const approachInner = radius - TOKENS.approach.width / 2;
    this.ring = createSdfCircle(approachOuter, approachInner);
    this.ring.tint = accent;
    this.ring.scale.set(this.startScale);
    this.ring.alpha = TOKENS.glowMed;
    this.addChild(this.ring);

    const ticker = () => {
      const now = GameClock.now();
      const elapsed = now - this.appear;
      let alpha = Math.min(elapsed / fade, 1);
      if (hidden) {
        const fadeStart = preempt - fade;
        if (elapsed >= fadeStart) {
          alpha = Math.max(1 - (elapsed - fadeStart) / fade, 0);
        }
      }
      this.circle.alpha = TOKENS.glowStrong * alpha;
      this.ring.alpha = TOKENS.glowMed * alpha;
      const t = Math.min(elapsed / preempt, 1);
      const scale = this.startScale - (this.startScale - 1) * easeOutCubic(t);
      this.ring.scale.set(scale);
      if (now >= this.appear + preempt) {
        this.ring.destroy();
        app.ticker.remove(ticker);
      }
    };

    app.ticker.add(ticker);
  }

  /** Trigger a brief scale pulse animation. */
  pulse(): void {
    this.pulseStart = GameClock.now();
    if (!this.pulseTicker) {
      this.pulseTicker = () => {
        const t = (GameClock.now() - this.pulseStart) / this.pulseDuration;
        if (t >= 1) {
          this.circle.scale.set(1);
          this.app.ticker.remove(this.pulseTicker!);
          this.pulseTicker = undefined;
          return;
        }
        const scale = 1 + 0.05 * Math.sin(t * Math.PI);
        this.circle.scale.set(scale);
      };
      this.app.ticker.add(this.pulseTicker);
    }
  }
}

